import { memo } from "react"
import dynamic from "next/dynamic"
import { Grid, Table, TableCell, TableRow, TableBody, Theme, Box } from "@mui/material"

import Price from "components/Price"
import Dimensions from "components/Dimensions"
const Assets = dynamic(() => import("./Assets"), { ssr: false })

import useTranslation from "hooks/useTranslation"

import getSecurityLevelInsurances from "utils/getSecurityLevelInsurances"
import { makeStyles, withStyles } from "makeStyles"

import { ProductDetailPageReducerState } from "@secureo/common/reducers/productDetailPageReducer"
import { ProductAttributes, TechnicalDetails } from "@secureo/common/typings/ProductAttributes"
import { ProductTypeName } from "@secureo/common/typings/Product"
import { TFunction } from "next-i18next"
import { getLocalizedAttributeValue } from "containers/ProductDetailPage/ProductHeader/Attributes"

const useStyles = makeStyles()(() => ({
	// headline styles
	tabHeadline: {
		borderBottom: "5px solid #7abfac",
		marginBottom: "0",
		maxWidth: "90%",
	},
	tabHeadlineSecondBorder: {
		borderBottom: "1px solid lightgrey",
		marginBottom: "24px",
	},
	table: {
		minWidth: "100%",
	},
	headline: {
		fontWeight: "bold",
		textTransform: "uppercase",
	},
	download: {
		display: "flex",
		alignItems: "center",
		height: "auto",
		margin: "0.5rem 0",
	},
}))

const StyledTableCell = withStyles(TableCell, () => ({
	body: {
		fontSize: 14,
		color: "black",
	},
}))

const StyledTableRow = withStyles(TableRow, (theme: Theme) => ({
	root: {
		"&:nth-of-type(odd)": {
			backgroundColor: theme.palette.background.default,
			color: "black",
		},
	},
}))

const getSafeInsuranceInformation = (
	productType: ProductTypeName,
	attributes: ProductAttributes,
	t: TFunction,
) => {
	if (productType !== "Safe") return []
	const { fireSafetyGrade, securityGrade } = attributes

	const detailedSecurityGrade = getSecurityLevelInsurances(securityGrade)
	const insuranceSumPrivateEur = detailedSecurityGrade?.insuranceSumPrivateEur ?? 0
	const insuranceSumBusinessEur = detailedSecurityGrade?.insuranceSumBusinessEur ?? 0

	return [
		{
			iconUrl:
				"https://res.cloudinary.com/safehero/image/upload/v1571664488/Icons/shield-lock_zadbvk.png",
			headline: t("securityGrade"),
			info: t(securityGrade),
			background: "rgba(74, 142, 142, 0.2)",
		},
		{
			iconUrl:
				"https://res.cloudinary.com/safehero/image/upload/v1571664488/Icons/fire_ftoi27.png",
			headline: t("fireSafetyGrade"),
			info: t(fireSafetyGrade),
			background: "rgba(207, 66, 59, 0.2)",
		},
		{
			iconUrl:
				"https://res.cloudinary.com/safehero/image/upload/v1571664489/Icons/home-currency-usd_qct6d4.png",
			headline: t("insurancePrivate"),
			info: insuranceSumPrivateEur ? (
				<Price priceInEuroCents={insuranceSumPrivateEur} />
			) : (
				"-"
			),
			background: "rgba(27, 92, 113, 0.2)",
		},
		{
			iconUrl:
				"https://res.cloudinary.com/safehero/image/upload/v1571664489/Icons/home-currency-usd_qct6d4.png",
			headline: t("insuranceBusiness"),
			info: insuranceSumBusinessEur ? (
				<Price priceInEuroCents={insuranceSumBusinessEur} />
			) : (
				"-"
			),
			background: "rgba(27, 92, 113, 0.2)",
		},
	]
}

const getFeatures = (
	productType: ProductTypeName,
	attributes: ProductAttributes,
	technicalDetails: TechnicalDetails,
	t: TFunction,
): { name: string; value: string | number }[] => {
	if (productType === "Safe") {
		return [
			{
				name: t("manufacturer"),
				value: attributes.manufacturer,
			},
			{
				name: t("fireSafetyGrade"),
				value: attributes.fireSafetyGrade,
			},
			{
				name: t("securityGrade"),
				value: attributes.securityGrade,
			},
			{
				name: t("numberOfShelves"),
				value: attributes.numberOfShelves,
			},
			{
				name: t("numberOfWeaponFixtures"),
				value: attributes.numberOfWeaponFixtures,
			},
			{
				name: t("numberOfA4Folders"),
				value: attributes.numberOfA4Folders,
			},
			{
				name: t("anchoring"),
				value: t(attributes.anchoring),
			},
			{
				name: t("sufficientSpaceForA4Folders"),
				value: attributes.sufficientSpaceForA4Folders,
			},
			{
				name: t("color"),
				value: attributes?.color?.split(";")?.join(" & "),
			},
			{
				name: t("hinges"),
				value: t(attributes.positionOfHinges),
			},
			{
				name: t("doorSwingDirection"),
				value: t(attributes.doorSwingDirection),
			},
			{
				name: t("doorOpeningAngle"),
				value: attributes.doorOpeningAngle,
			},
		].filter((attribute) => Boolean(attribute?.value))
	}
	if (["simpleConfigurationProduct", "Zylinder"].includes(productType)) {
		return (
			Object.entries(technicalDetails ?? {}).map(([name, value]) => ({ name, value })) ?? []
		)
	}

	return []
}

const Description = ({ reducerState, language }: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")
	const {
		productType,
		description,
		attributes,
		locale,
		assets: masterVariantAssets,
		safeLockConfigurationProductsBySku,
	} = reducerState

	const assets =
		Object.values(safeLockConfigurationProductsBySku ?? {})?.reduce(
			(safeLockConfigurationAssets, { assets = [] }) => {
				return [...safeLockConfigurationAssets, ...assets]
			},
			masterVariantAssets,
		) ?? masterVariantAssets

	const {
		weightKg,
		volumeLiter,
		technicalDetails,
		innerWidth,
		innerHeight,
		innerDepth,
		outerWidth,
		outerHeight,
		outerDepth,
	} = attributes

	const safeInsuranceInformation = getSafeInsuranceInformation(productType, attributes, t)
	const features = getFeatures(productType, attributes, technicalDetails, t)

	const dimensions =
		productType === "Safe"
			? [
					{
						name: t("outerDimensionsInCm"),
						value: (
							<Dimensions
								width={outerWidth}
								height={outerHeight}
								depth={outerDepth}
								locale={locale}
							/>
						),
					},
					{
						name: t("innerDimensionsInCm"),
						value: (
							<Dimensions
								width={innerWidth}
								height={innerHeight}
								depth={innerDepth}
								locale={locale}
							/>
						),
					},
					{
						name: t("weightKg"),
						value: weightKg,
					},
					{
						name: t("volumeLiter"),
						value: volumeLiter,
					},
				].filter((attribute) => Boolean(attribute?.value))
			: []

	const showFeatures = features.length > 0
	const showDimensions = dimensions.length > 0
	const filteredAssets = assets.filter((asset) => Boolean(asset?.name))
	const showAssets = filteredAssets.length > 0
	const showSafeInsuranceInformation = Boolean(
		safeInsuranceInformation.length > 0 && (showFeatures || showDimensions),
	)

	return (
		<Grid container spacing={3}>
			<Grid item xs={12} md={12}>
				{description && (
					<div
						style={{ margin: "0.5rem 0" }}
						dangerouslySetInnerHTML={{ __html: description }}
					/>
				)}
				<div className={classes.tabHeadlineSecondBorder}>
					<h2 className={classes.tabHeadline}>{t("technicalData")}</h2>
				</div>
				{showSafeInsuranceInformation && language === "de" && (
					<Grid container spacing={3}>
						{safeInsuranceInformation.map((attribute) => {
							const { background, iconUrl, headline, info } = attribute
							return (
								<Grid item xs={12} sm={6} lg={3} key={headline}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											backgroundColor: `${background}`,
											padding: 15,
											borderRadius: 5,
										}}
									>
										<img
											alt={headline}
											src={iconUrl}
											style={{ maxWidth: "30px", marginRight: "10px" }}
										/>
										<div>
											<div>
												<strong>{headline}</strong>
											</div>
											<div>{info}</div>
										</div>
									</div>
								</Grid>
							)
						})}
					</Grid>
				)}
				<Grid container spacing={3}>
					<Grid item xs={12} md={7}>
						{showFeatures && (
							<>
								<Box mt={3} mb={1} className={classes.headline}>
									{t("keyFeatures")}
								</Box>
								<div style={{ overflowX: "scroll" }}>
									<Table className={classes.table} size="small">
										<TableBody>
											{features.map((row) => (
												<StyledTableRow key={row.name}>
													<StyledTableCell component="th" scope="row">
														{row.name}
													</StyledTableCell>
													<StyledTableCell align="right">
														{getLocalizedAttributeValue(
															row.value as any,
															language,
														)}
													</StyledTableCell>
												</StyledTableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</>
						)}
						{showDimensions && (
							<>
								<Box mt={3} mb={1} className={classes.headline}>
									{t("dimensions")}
								</Box>
								<Table className={classes.table} size="small">
									<TableBody>
										{dimensions.map((row) => {
											const { name, value } = row
											return (
												<StyledTableRow key={name}>
													<StyledTableCell component="th" scope="row">
														{name}
													</StyledTableCell>
													<StyledTableCell align="right">
														{value}
													</StyledTableCell>
												</StyledTableRow>
											)
										})}
									</TableBody>
								</Table>
							</>
						)}
					</Grid>
					<Grid item xs={12} md={showDimensions || showFeatures ? 5 : 12}>
						{showAssets && (
							<Assets
								headlineClass={classes.headline}
								downloadClass={classes.download}
								assets={filteredAssets}
							/>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

interface Props {
	reducerState: ProductDetailPageReducerState
	language: string
}

export default memo(Description)
